import { Alert, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { ConversationResponse } from "@bw/shared/types/generated";
import { useCreateConversation } from "../hooks/useCreateConversation";
import { useFriendActions } from "../hooks/useFriendActions";
import { useFriendRequests } from "../hooks/useFriendRequests";
import { useFriends } from "../hooks/useFriends";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

const buttonSx = { borderRadius: 999, flex: { xs: 1, sm: "0 0 auto" } } as const;

/**
 * The friendship control on another player's profile, from the server's
 * friends list and request inbox: Add friend, Accept/Decline, Cancel request,
 * or Message once they are friends (direct messages require friendship).
 */
export function ProfileFriendActions({ playerId, playerName }: { playerId: string; playerName: string }) {
  const navigate = useNavigate();
  const friendsQ = useFriends();
  const requestsQ = useFriendRequests();
  const { accept, decline, cancel, sendRequest, isRequestBusy, isSendBusy, resetCreate, resetInboxErrors } =
    useFriendActions();
  const createConversation = useCreateConversation(resetInboxErrors);
  resetCreate.current = createConversation.reset;

  if (friendsQ.isLoading || requestsQ.isLoading)
    return <Button disabled variant="outlined" sx={buttonSx}>Checking…</Button>;
  if (friendsQ.isError || requestsQ.isError)
    return <Alert severity="error">Friendship status is unavailable.</Alert>;

  const isFriend = (friendsQ.data ?? []).some((friend) => friend.user_id === playerId);
  const incoming = requestsQ.data?.incoming.find((request) => request.user_id === playerId);
  const outgoing = requestsQ.data?.outgoing.find((request) => request.user_id === playerId);
  const error = accept.error ?? decline.error ?? cancel.error ?? sendRequest.error ?? createConversation.error;
  const openConversation = (conversation: ConversationResponse) => {
    if (conversation.conversation_id) navigate(`/community/messages/${conversation.conversation_id}`);
  };

  let actions;
  if (isFriend) {
    actions = (
      <Button
        variant="contained"
        sx={buttonSx}
        aria-label={`Message ${playerName}`}
        disabled={createConversation.isPending}
        onClick={() => createConversation.mutate(playerId, { onSuccess: openConversation })}
      >
        Message
      </Button>
    );
  } else if (incoming) {
    const busy = isRequestBusy(incoming.friendship_id);
    actions = (
      <>
        <Button variant="contained" sx={buttonSx} disabled={busy} onClick={() => accept.mutate(incoming.friendship_id)}>
          Accept request
        </Button>
        <Button variant="outlined" color="inherit" sx={buttonSx} disabled={busy} onClick={() => decline.mutate(incoming.friendship_id)}>
          Decline
        </Button>
      </>
    );
  } else if (outgoing) {
    actions = (
      <Button
        variant="outlined"
        color="inherit"
        sx={buttonSx}
        disabled={isRequestBusy(outgoing.friendship_id)}
        onClick={() => cancel.mutate(outgoing.friendship_id)}
      >
        Cancel request
      </Button>
    );
  } else {
    actions = (
      <Button
        variant="contained"
        sx={buttonSx}
        aria-label={`Add friend: ${playerName}`}
        disabled={isSendBusy(playerId)}
        onClick={() => sendRequest.mutate(playerId)}
      >
        Add friend
      </Button>
    );
  }
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>{actions}</Stack>
      {error ? (
        <GuardianAwareError error={error}>
          <Alert severity="error" role="alert">That didn&apos;t work. Please try again.</Alert>
        </GuardianAwareError>
      ) : null}
    </Stack>
  );
}
